import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import axiosClient from '../api/axiosClient';
import { useToast } from '../context/ToastContext';

const GetInTouchSection = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const { addToast } = useToast()

  // Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    try {
      // Replace with actual form submission logic
      await axiosClient.post('/contact-us', formData)
      addToast({
        type: "success",
        title: "Success !",
        content: "Message sent successfully!"
      })
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      addToast({
        type: "error",
        title: "Error !",
        content: "Failed to send message. Please try again."
      })
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 px-4 bg-purple-50">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Get in Touch</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Have questions, feedbacks, or partnership inquiries? We'd love to hear from you! Reach out to us, and our team will get back to you as soon as possible.
          </p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Icon icon="mdi:account-outline" className="text-gray-400" />
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="py-2 pl-10 pr-3 block w-full rounded-md bg-white border-gray-200 focus:ring-primary-purple-200 focus:border-primary-purple-200"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Icon icon="mdi:email-outline" className="text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your email"
                  className="py-2 pl-10 pr-3 block w-full rounded-md bg-white border-gray-200 focus:ring-primary-purple-200 focus:border-primary-purple-200"
                  required
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="6"
              placeholder="Enter message"
              className="py-2 px-3 block w-full rounded-md bg-white border-gray-200 focus:ring-primary-purple-200 focus:border-primary-purple-200"
              required
            ></textarea>
          </div>

          <div className="flex justify-center">
            <motion.button
              type="submit"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-full text-white bg-primary-purple-200 hover:bg-primary-purple-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-purple-200 transition-colors"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Icon icon="mdi:loading" className="animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  Send message
                  <Icon icon="mdi:send" className="ml-2" />
                </>
              )}
            </motion.button>
          </div>
        </form>

        {/* Alternative Contact Methods */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="text-center md:text-left">
            <h3 className="text-lg font-medium mb-2">Chat to us</h3>
            <p className="text-gray-600 text-sm mb-1">Our friendly team is here to help 24/7</p>
            <a href="mailto:support@bildup.ai" className="text-primary-purple-400 font-medium hover:underline">
              support@bildup.ai
            </a>
          </div>

          <div className="text-center md:text-left">
            <h3 className="text-lg font-medium mb-2">Call us</h3>
            <p className="text-gray-600 text-sm mb-1">Our toll-free line available 7am - 6pm</p>
            <a href="tel:+2348135433777" className="text-primary-purple-400 font-medium hover:underline">
              +2348135433777
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GetInTouchSection;
